/**
 * Regras únicas das skills na iVFS (ADR-070).
 *
 * A skill é um `.md` direto na classe da área. O tipo (curadoria | atuacao) e a
 * situação (active | draft) ficam no `metadata` do frontmatter, o único ponto de
 * extensão da especificação Agent Skills (https://agentskills.io/specification).
 * Os arquivos de apoio da skill (references/, scripts/, assets/) usam pasta
 * virtual no nome, com `~` no lugar de `/`.
 *
 * Sem dependência de YAML de propósito: o pacote é usado pelo engine, pelo
 * gateway e pelo portal. O `metadata` é lido por linha, nas duas formas que o
 * YAML permite (bloco e fluxo).
 */
/** Separador do caminho virtual: `nome~references~x.md` ⇄ `nome/references/x.md`. */
export const SEPARADOR_CAMINHO_SKILL = '~';
/** Nome do arquivo principal da skill na especificação. */
export const ARQUIVO_PRINCIPAL_SKILL = 'SKILL.md';
/** Chaves próprias do FLUX dentro de `metadata`. */
export const METADATA_FLUX_TIPO = 'flux-tipo';
export const METADATA_FLUX_STATUS = 'flux-status';
/**
 * Acentos e equivalentes, na mesma ordem, para `translate()` do Postgres.
 * Usado na busca de skill por nome sem acento (a migração tirou os acentos de
 * dois nomes, e o histórico guarda os nomes antigos).
 */
export const ACENTOS_SQL = 'áàâãäåéèêëíìîïóòôõöúùûüçñ';
export const SEM_ACENTOS_SQL = 'aaaaaaeeeeiiiiooooouuuucn';
/** Nome de skill válido pela especificação: minúsculas, dígitos e hífen simples. */
export function nomeSkillValido(nome) {
    return nome.length >= 1 && nome.length <= 64 && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(nome);
}
/** Minúsculas e sem acento — para comparar nomes antigos e novos. */
export function normalizarNomeSkill(nome) {
    return nome.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
/** Arquivo de apoio de uma skill (tem `~` no nome) — nunca é skill por si. */
export function ehRecursoDeSkill(iname) {
    return iname.includes(SEPARADOR_CAMINHO_SKILL);
}
/** Nome da skill dona do arquivo: o prefixo antes do primeiro `~`, sem `.md`. */
export function skillDonaDoArquivo(iname) {
    const base = iname.split(SEPARADOR_CAMINHO_SKILL)[0];
    return base.replace(/\.md$/i, '');
}
/**
 * Nome na iVFS → caminho na especificação.
 *   `triar-assinaturas.md` → `triar-assinaturas/SKILL.md`
 *   `triar-assinaturas~references~exemplos.md` → `triar-assinaturas/references/exemplos.md`
 */
export function caminhoDaSkill(iname) {
    if (!ehRecursoDeSkill(iname)) {
        return `${iname.replace(/\.md$/i, '')}/${ARQUIVO_PRINCIPAL_SKILL}`;
    }
    return iname.split(SEPARADOR_CAMINHO_SKILL).join('/');
}
/** Caminho na especificação → nome na iVFS (inverso de `caminhoDaSkill`). */
export function nomeIvfsDoCaminho(caminho) {
    const partes = caminho.split('/').filter(Boolean);
    if (partes.length === 2 && partes[1] === ARQUIVO_PRINCIPAL_SKILL)
        return `${partes[0]}.md`;
    return partes.join(SEPARADOR_CAMINHO_SKILL);
}
/** Troca CRLF/CR por LF. */
export function normalizarFimDeLinha(content) {
    return content.replace(/\r\n?/g, '\n');
}
/** Tira BOM e espaço/linha em branco antes do `---` inicial (o carregador antigo tolerava). */
function normalizarInicio(content) {
    return content.replace(/^\uFEFF/, '').replace(/^\s+(?=---)/, '');
}
/** Frontmatter separado em abertura, linhas, fechamento e corpo (LF, sem BOM). */
function partesFrontmatter(content) {
    const texto = normalizarFimDeLinha(normalizarInicio(content));
    const m = texto.match(/^(---[ \t]*\n)([\s\S]*?)(\n---[ \t]*(?:\n|$))/);
    if (!m)
        return null;
    return { abre: m[1], linhas: m[2].split('\n'), fecha: m[3], corpo: texto.slice(m[0].length) };
}
function semAspas(v) {
    return v.trim().replace(/^(['"])(.*)\1$/, '$2').trim();
}
function semComentario(v) {
    return v.replace(/[ \t]+#.*$/, '');
}
/**
 * Localiza `metadata` nas linhas do frontmatter. Aceita bloco (com linha em branco,
 * comentário e comentário na própria linha de `metadata:`) e fluxo, inclusive de
 * várias linhas (`metadata: {` ... `}`).
 */
function localizarMetadata(linhas) {
    const i = linhas.findIndex(l => /^metadata[ \t]*:/.test(l));
    if (i < 0)
        return null;
    const mapa = {};
    const resto = semComentario(linhas[i].replace(/^metadata[ \t]*:/, '')).trim();
    if (resto.startsWith('{')) {
        let texto = resto;
        let j = i;
        while (!texto.includes('}') && j + 1 < linhas.length) {
            j++;
            texto += ' ' + semComentario(linhas[j]).trim();
        }
        const corpo = texto.replace(/^\{/, '').replace(/\}[\s\S]*$/, '');
        for (const par of corpo.split(',')) {
            const k = par.indexOf(':');
            if (k > 0)
                mapa[semAspas(par.slice(0, k))] = semAspas(par.slice(k + 1));
        }
        return { inicio: i, fim: j, forma: 'fluxo', recuo: '  ', mapa };
    }
    if (resto !== '') {
        // `metadata: valor` não é mapa: tratado como fluxo vazio (é substituído inteiro)
        return { inicio: i, fim: i, forma: 'fluxo', recuo: '  ', mapa };
    }
    let fim = i;
    let recuo = '';
    for (let k = i + 1; k < linhas.length; k++) {
        const linha = linhas[k];
        if (linha.trim() === '')
            continue; // linha em branco dentro do bloco
        if (!/^[ \t]+\S/.test(linha))
            break; // próxima chave de topo
        fim = k;
        if (/^[ \t]+#/.test(linha))
            continue; // comentário
        if (!recuo)
            recuo = linha.match(/^([ \t]+)/)[1];
        const m = linha.match(/^[ \t]+([^:#][^:]*):(.*)$/);
        if (m)
            mapa[semAspas(m[1])] = semAspas(semComentario(m[2]));
    }
    return { inicio: i, fim, forma: 'bloco', recuo: recuo || '  ', mapa };
}
/** Valor de `metadata` seguro em YAML: entre aspas duplas se tiver caractere especial. */
function valorYaml(v) {
    return /^$|[:#{}\[\],&*?|<>=!%@`'"]|^\s|\s$/.test(v) ? JSON.stringify(v) : v;
}
/** Mapa `metadata` do frontmatter, nas formas bloco e fluxo. Vazio se não houver. */
export function metadataDaSkill(content) {
    const p = partesFrontmatter(content);
    if (!p)
        return {};
    return localizarMetadata(p.linhas)?.mapa ?? {};
}
/** Tipo da skill: `metadata.flux-tipo`. Ausente → `atuacao`. */
export function tipoDaSkill(content) {
    const v = metadataDaSkill(content)[METADATA_FLUX_TIPO]?.toLowerCase();
    return v === 'curadoria' ? 'curadoria' : 'atuacao';
}
/** `metadata.flux-status: draft` — a skill não é carregada pelo FLUX. */
export function skillEmRascunho(content) {
    return metadataDaSkill(content)[METADATA_FLUX_STATUS]?.toLowerCase() === 'draft';
}
/** Tem frontmatter (começa com `---`, tolerando BOM e espaço antes)? */
export function temFrontmatter(content) {
    return /^---[ \t]*\r?\n/.test(normalizarInicio(content));
}
/**
 * Cabeçalho de skill (ADR-070 §2.2): frontmatter com `name` preenchido e `description`.
 * Um `.md` de documentação com frontmatter próprio (ex.: só `title:`) não é skill.
 */
export function cabecalhoDeSkillValido(content) {
    const p = partesFrontmatter(content);
    if (!p)
        return false;
    return p.linhas.some(l => /^name[ \t]*:[ \t]*\S/.test(l))
        && p.linhas.some(l => /^description[ \t]*:/.test(l));
}
/**
 * Garante `metadata.flux-tipo` no frontmatter, sem mexer no resto. Se já houver
 * `flux-tipo`, devolve o conteúdo igual. Bloco existente: acrescenta a chave com o
 * mesmo recuo. Fluxo: reescreve como bloco, preservando as chaves. Sem `metadata`:
 * cria o bloco antes do `---` final. Sem frontmatter, devolve igual (não é skill).
 */
export function garantirMetadataFlux(content, tipo) {
    const p = partesFrontmatter(content);
    if (!p)
        return content;
    const loc = localizarMetadata(p.linhas);
    if (loc?.mapa[METADATA_FLUX_TIPO])
        return content;
    const linhas = [...p.linhas];
    if (!loc) {
        linhas.push('metadata:', `  ${METADATA_FLUX_TIPO}: ${tipo}`);
    }
    else if (loc.forma === 'bloco') {
        linhas.splice(loc.inicio + 1, 0, `${loc.recuo}${METADATA_FLUX_TIPO}: ${tipo}`);
    }
    else {
        const bloco = ['metadata:', `  ${METADATA_FLUX_TIPO}: ${tipo}`,
            ...Object.entries(loc.mapa).map(([k, v]) => `  ${k}: ${valorYaml(v)}`)];
        linhas.splice(loc.inicio, loc.fim - loc.inicio + 1, ...bloco);
    }
    return `${p.abre}${linhas.join('\n')}${p.fecha}${p.corpo}`;
}
/** Frontmatter inicial de uma skill nova no Studio. */
export function frontmatterInicialSkill(nome, tipo = 'curadoria') {
    return `---\nname: ${nome}\ndescription: >\n  \nmetadata:\n  ${METADATA_FLUX_TIPO}: ${tipo}\n---\n\n`;
}
//# sourceMappingURL=skills.js.map